import type {
  QuoteSearchResponse,
  QuoteStatusChangePostRequestParams,
  QuoteStatusChangePostRequest,
  QuoteStatusChangePostResponse,
} from '@lio/api-types';
import { useQueryClient, useMutation, InfiniteData } from '@tanstack/react-query';
import { queryKeys as underwritingReferralQueryKeys } from '@services/underwritingReferral';
import { useClient } from '@utils/api-client';

export const useCreateQuoteStatusChange = () => {
  const client = useClient();
  const queryClient = useQueryClient();
  const mutationFn = ({
    quoteId,
    statusId,
    shouldSkipEmail,
    ...quoteStatusChangeReason
  }: QuoteStatusChangePostRequestParams & QuoteStatusChangePostRequest) => {
    return client
      .post(`quote/${quoteId}/status-change`, {
        json: { statusId, ...quoteStatusChangeReason, shouldSkipEmail },
      })
      .json<QuoteStatusChangePostResponse>();
  };
  const result = useMutation({
    mutationFn,
    onMutate: async (updatedData: QuoteStatusChangePostRequestParams & QuoteStatusChangePostRequest) => {
      const queryKey = ['quotes'];
      await queryClient.cancelQueries({ queryKey });
      const previousQuotes = queryClient.getQueriesData<InfiniteData<QuoteSearchResponse>>({ queryKey });
      queryClient.setQueriesData<InfiniteData<QuoteSearchResponse>>(
        { queryKey },
        (previousState) =>
          ({
            ...previousState,

            pages: previousState?.pages?.map((page) => ({
              ...page,
              items: page.items?.map((q) => {
                if (q.id === updatedData.quoteId) {
                  return { ...q, statusId: updatedData.statusId };
                }
                return q;
              }),
            })),
          }) as InfiniteData<QuoteSearchResponse>,
      );
      return { previousQuotes };
    },
    onSuccess: async ({ quoteId }: QuoteStatusChangePostRequestParams) => {
      const queryKey = ['quote', { quoteId }];
      await queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: [underwritingReferralQueryKeys.underwritingActivity] });
    },
  });
  return { ...result, createQuoteStatusChange: result.mutateAsync };
};
